import {
  Group,
  Line,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  PointLight,
  Scene,
  WebGLRenderer} from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';

import {debounce, lerp} from '../../../static/js/lib/utilities';

const MOUSEMOVE_DELTA = Math.PI / 4 * -1;

const meshMaterial = new MeshBasicMaterial( {color: 0x020202} );
const lineMaterial = new LineBasicMaterial( {color: 0xffffff} );

/**
 * @param {THREE.Geometry} geometry
 * @param {Class} LineClass
 * @return {THREE.Group} group
 */
function getGeometryGroup( geometry, LineClass = LineSegments) {
  const group = new Group();
  const mesh = new Mesh( geometry, meshMaterial );
  const line = new LineClass( geometry, lineMaterial );
  group.add( mesh );
  group.add( line );
  return group;
}

/**
 * @param {Error} error
 */
function handleLoaderError(error) {
  // eslint-disable-next-line no-console
  console.error('Error loading file for THREE.js', error);
}

export default class TestimonialScene {
  constructor() {
    this.isAnimating = false;
    this.isInitialized = false;

    // rebind
    this.animate = this.animate.bind(this);
    this.onMousemove = this.onMousemove.bind(this);
    this.onResize = debounce(this.onResize.bind(this));
  }

  startAnimation() {
    if (this.isInitialized && this.isAnimating) {
      return;
    } else if (this.isInitialized) {
      this.initAnimation();
    } else {
      this.initialize(() => {
        this.initAnimation();
      });
    }
  }

  stopAnimation() {
    this.isAnimating = false;
  }

  /**
   * initialize animation
   */
  initAnimation() {
    this.isAnimating = true;
    this.animate();
  }

  animate() {
    if (this.groupPivot) {
      this.groupPivot.rotation.x += 0.005;
      this.groupPivot.rotation.z += 0.004;
    }
    if (this.renderer) this.renderer.render( this.scene, this.camera );

    if (this.isAnimating) requestAnimationFrame( this.animate );
  }

  /**
   * @param {Function} onReady - called when models loaded
   */
  initialize(onReady) {
    if (this.isInitialized) return;

    this.isInitialized = true;
    const $container = document.querySelector('[data-js="TestimonialScene"]');
    this.windowWidth = window.innerWidth;
    this.windowHeight = window.innerHeight;
    const isMinQueryM = this.windowWidth >= 769;

    const scene = this.scene = new Scene();

    const cameraZ = isMinQueryM ? 900 : 1500;
    const camera = this.camera = new PerspectiveCamera(
      45,
      this.windowWidth / this.windowHeight,
      1,
      cameraZ + 1000,
    );
    camera.position.set( 0, 0, cameraZ );

    const renderer = this.renderer = new WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    renderer.setClearColor( 0x000000, 0 ); // the default
    renderer.setPixelRatio( window.devicePixelRatio );
    renderer.setSize( this.windowWidth, this.windowHeight );
    $container.append( renderer.domElement );

    const groupPivot = this.groupPivot = new Group();

    scene.add( groupPivot );

    const light = new PointLight(0xffffff, 1, 0);
    light.position.set(1, 1, 80);
    scene.add(light);

    const loader = new GLTFLoader();
    loader.load('/static/img/donut_noholes.gltf', (gltf) => {
      const donutGeometry = gltf.scene.children[0].geometry;
      const donutGroup = getGeometryGroup( donutGeometry );
      donutGroup.position.y = 100;
      donutGroup.position.x = -50;
      donutGroup.rotation.z = 1;
      groupPivot.add( donutGroup );
    }, undefined, handleLoaderError);

    loader.load('/static/img/ring_tris2.gltf', (gltf) => {
      const ringGeometry = gltf.scene.children[0].geometry;
      const ringGroup = getGeometryGroup( ringGeometry, Line );
      ringGroup.position.y = -100;
      ringGroup.position.x = -30;
      ringGroup.rotation.z = -0.2;
      groupPivot.add( ringGroup );
    }, undefined, handleLoaderError);

    loader.load('/static/img/cylinder3.gltf', (gltf) => {
      const cylinderGeometry = gltf.scene.children[0].geometry;
      const cylinderGroup = getGeometryGroup( cylinderGeometry );

      cylinderGroup.position.x = 150;
      cylinderGroup.position.y = 50;
      cylinderGroup.rotation.x = 1;
      groupPivot.add( cylinderGroup );
    }, undefined, handleLoaderError);

    window.addEventListener('resize', this.onResize );
    window.addEventListener('mousemove', this.onMousemove );

    if (onReady) onReady();
  }

  /**
   * @param {Number} x
   * @param {Number} y
   */
  setModelsPosition(x, y) {
    if (this.scene) this.scene.position.set(x, y);
  }

  onResize() {
    this.camera.updateProjectionMatrix();
    this.windowWidth = window.innerWidth;
    this.windowHeight = window.innerHeight;
    this.renderer.setSize( this.windowWidth, this.windowHeight );
  }

  /**
   * mousemove event listener
   * @param {Event} event
   */
  onMousemove(event) {
    const alphaX = event.clientX / this.windowWidth;
    const alphaY = event.clientY / this.windowHeight;
    this.scene.rotation.x = lerp( -1, 1, alphaY ) * MOUSEMOVE_DELTA;
    this.scene.rotation.y = lerp( -1, 1, alphaX ) * MOUSEMOVE_DELTA;
  }

  unmount() {
    window.removeEventListener('resize', this.onResize );
    window.removeEventListener('mousemove', this.onMousemove );
  }
}
